import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService }                    from '@ngx-translate/core';
import { UIRouter }                            from '@uirouter/core';
import { Subscription }                        from 'rxjs';

import { BindHtmlComponent }                from '../../table/cell-components/bind-html.component';
import { DateParserService }                from '../../core/services/date-parser.service';
import { Table }                            from '../../table/types/table';
import { TableQueryParameters }             from '../../table/types/table-query-parameters';
import { TableService }                     from '../../table/table.service';
import { TransactionCenterBookingModel }    from './typings/transaction-center.booking';
import { TransactionCenterBookingsService } from './transaction-center.bookings.service';
import { TransactionCenterFiltersModel }    from '../typings/transaction-center.filters';
import { TransactionCenterService }         from '../transaction-center.service';

@Component({
  selector   : 'wor-transaction-center-bookings',
  template: require('./transaction-center.bookings.component.html')
})
export class TransactionCenterBookingsComponent implements OnDestroy, OnInit {
  @Input() bookings: Array<TransactionCenterBookingModel> = [];

  broadcastKey  = this.bookingsService.broadcastKey;
  tableCacheKey = this.bookingsService.tableCacheKey;

  ranges = [
    {
      language_label: 'JS_SPACE.TABS.MONTH',
      name          : 'month'
    },
    {
      language_label: 'JS_SPACE.TABS.WEEK',
      name          : 'week'
    },
    {
      language_label: 'GENERAL_SPACE.BOOKING_DAYS.TODAY',
      name          : 'today'
    }
  ];

  filters : TransactionCenterFiltersModel = this.tableService.setFiltersFromCache(this.tableCacheKey, {
    endDate  : this.dateParserService.weekRangeEnd(null),
    range    : 'week',
    startDate: this.dateParserService.weekRangeStart(null),
    value    : ''
  });

  tableSettings : Table = {
    broadcast: this.broadcastKey,
    cache    : {
      enabled: true,
      key    : this.tableCacheKey
    },
    columns: [
      {
        field : 'event_date',
        header: {
          text: this.translateService.instant('JS_SPACE.COLUMNS.EVENT_DATE')
        },
        pipe: 'globalDate'
      },
      {
        field : 'customer_display_name',
        header: {
          text: this.translateService.instant('CUSTOMER_SPACE.CUSTOMER')
        }
      },
      {
        component: {
          bindings: {
            inputs: {
              model: 'customer_mobile',
              pipe : 'mobilePhone'
            }
          },
          enabled: true,
          factory: BindHtmlComponent
        },
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.MOBILE')
        }
      },
      {
        field : 'vehicle_make',
        header: {
          text: this.translateService.instant('VEHICLE_SPACE.MAKE')
        }
      },
      {
        field : 'vehicle_model',
        header: {
          text: this.translateService.instant('VEHICLE_SPACE.MODEL')
        }
      },
      {
        field : 'description',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION')
        }
      },
      {
        classes: 'text-right',
        field  : 'estimated_hours',
        header : {
          classes: 'text-right',
          text   : this.translateService.instant('BOOKING_SPACE.ESTIMATED_HOURS')
        }
      },
      {
        classes  : 'text-left',
        component: {
          bindings: {
            inputs: {
              model: 'event_status',
              pipe : 'eventStatus'
            }
          },
          enabled: true,
          factory: BindHtmlComponent
        },
        header: {
          classes: 'text-left',
          text   : this.translateService.instant('GENERAL_SPACE.FIELD.STATUS')
        },
        sortable: false
      }
    ],
    empty: {
      text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_BOOKINGS_FOUND')
    },
    pagination: {
      enabled   : true,
      pageSize  : this.tableService.getCachedPaging(this.tableCacheKey).pageSize || 10,
      pageSizes : [ 10, 25, 50 ],
      queryCount: () => this.bookingsService.getCount(this.filters)
    },
    rows: {
      highlight: {
        enabled: true
      },
      select: {
        enabled: true
      }
    },
    sorting: this.tableService.setSortingFromCache(this.tableCacheKey, {
      direction: 'desc',
      field    : 'event_date'
    })
  };

  searchSubscription: Subscription;

  constructor (
    private bookingsService         : TransactionCenterBookingsService,
    private dateParserService       : DateParserService,
    private router                  : UIRouter,
    private tableService            : TableService,
    private transactionCenterService: TransactionCenterService,
    private translateService        : TranslateService
  ) {}

  ngOnDestroy () : void {
    this.searchSubscription?.unsubscribe();
  }

  ngOnInit () : void {
    this.transactionCenterService.setSearch(this.filters.value || '');

    this.searchSubscription = this.transactionCenterService.searchChanges
      .subscribe(value => {
        if ( value !== null && value !== this.filters.value ) {
          this.filters.value = value;

          this.filterTable();
        }
      });
  }

  filterTable () : void {
    if ( window[ this.broadcastKey ] ) {
      window[ this.broadcastKey ](this.filters);
    }
  }

  getBookings ( params : TableQueryParameters ) : Promise<Array<TransactionCenterBookingModel>> {
    return this.bookingsService.get(this.filters, params)
      .then(( bookings : Array<TransactionCenterBookingModel> ) => {
        this.bookings = bookings;

        return bookings;
      });
  }

  setDateRange ( range : string ) : void {
    this.filters.range = range;

    switch ( range ) {
      case 'month':
        this.filters.endDate   = this.dateParserService.thisMonthEnd();
        this.filters.startDate = this.dateParserService.thisMonthStart();

        break;
      case 'today':
        this.filters.endDate   = this.dateParserService.today();
        this.filters.startDate = this.dateParserService.today();

        break;
      case 'week':
      default:
        this.filters.endDate   = this.dateParserService.weekRangeEnd(null);
        this.filters.startDate = this.dateParserService.weekRangeStart(null);

        break;
    }

    this.filterTable();
  }

  viewBooking ( booking : TransactionCenterBookingModel ) : void {
    this.router.stateService.go('app.event', {
      customer_id: booking.customer_id,
      id         : booking.event_id,
      vehicle_id : booking.vehicle_id
    });
  }
}
